import axios from "axios";
// import { useToast } from "@chakra-ui/react";
import { omit, cloneDeep } from "lodash";
import hotToast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { CollaborationAppGroup } from "../models/collaborate.model.ts";
import TokenManager from "../providers/auth/token-manager.ts";
import {
    populateCampaignTemplates,
    setCampaignLoading,
    setCampaignLoadingError,
    populateCampaigns,
    setCampaignsLoading,
    setCampaignsLoadingError
} from "../store/slices/campaign.slice.ts";
import {
    populateCatalogues,
    setCatalogueLoading,
    setCatalogueLoadingError
} from "../store/slices/catalogue.slice.ts";
import {
    populateClients,
    setClientLoading,
    setClientLoadingError
} from "../store/slices/client.slice.ts";
import {
    populateEvents,
    setEventLoading,
    setEventLoadingError
} from "../store/slices/event.slice.ts";
import {
    setTools,
    setMemories,
    setCollaborationApp,
    NodeState
} from "../store/slices/node.slice.ts";
import {
    populatePersonas,
    setPersonaLoading,
    setPersonaLoadingError
} from "../store/slices/persona.slice.ts";
import {
    populateTemplates,
    setTemplateLoading,
    setTemplateLoadingError
} from "../store/slices/template.slice.ts";
import { ICampaign } from "../types/campaign.ts";
import { IClient } from "../types/client.type.ts";
import { PersonaModel } from "../types/persona.ts";
import { TemplateMetadataModel } from "../types/template.ts";
import {
    ITransactionCoordinatorCatalogue,
    ITransactionCoordinatorEvent
} from "../types/transaction-coordinator.ts";
import { getApplicationCode } from "../utils/auth.utils";

const httpClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL
});

const applicationCode = getApplicationCode();

export function useHttpClient() {
    // const toast = useToast();
    const dispatch = useDispatch();
    const nodesDataState: NodeState = useSelector((state: any) => state.nodeData);
    // const token = useSelector(selectAuthToken);

    httpClient.interceptors.request.use(
        async (config) => {
            const tokenManager = TokenManager.getInstance();
            const token = await tokenManager.getToken();
            if (!token) {
                return Promise.reject(new Error("No Authentication token found"));
            }
            config.headers["Authorization"] = `Bearer ${token}`;

            // Set vendor Id
            // eslint-disable-next-line no-undef
            const vendorId = window.sessionStorage.getItem("vendorId");

            if (vendorId) {
                config.headers["Neo-Vendor-Id"] = vendorId;
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    async function listMemoryApi() {
        try {
            // const { data } = await httpClient.post(`/api/v1/data-upload/list-data-upload?access_token=${token}`);

            // if (data.success === false) {
            //     throw new Error("Failed to list memories");
            // } else {
            //     if (data.length) {
            //         for (const memory of data) {
            //             const memories: Array<{
            //                 id: string;
            //                 sector_id: string;
            //                 namespace: string;
            //                 sector: string;
            //                 brain_type: string;
            //                 is_structured: boolean;
            //                 sector_display: string;
            //                 sector_description: string;
            //             }> = [];

            //             const sectorIndex = memories.findIndex(
            //                 (sectorItem) =>
            //                     sectorItem.sector_id === memory.data_sector &&
            //                     sectorItem.brain_type === memory.brain_type
            //             );

            //             if (sectorIndex < 0) {
            //                 memories.push({
            //                     id: memory.id,
            //                     sector_id: memory.data_sector,
            //                     namespace: memory.namespace,
            //                     sector: memory.sector,
            //                     brain_type: memory.brain_type,
            //                     is_structured: memory.is_structured,
            //                     sector_display: memory.sector_display,
            //                     sector_description: memory.sector_description,
            //                 });
            //             }

            //             dispatch(setMemories(memories));
            //         }
            //     }
            // }

            dispatch(setMemories([]));
        } catch (e) {
            console.error(e);
            // Show error toast
            hotToast.error(
                "We are unable to load the memories at the moment. Please try again later."
            );
        }
    }

    async function listToolApi() {
        try {
            const { data } = await httpClient.post(`/api/v1/tool/list-tool`, {
                data: {
                    search_criteria: "",
                    pagination: {
                        rows_per_page: 50,
                        page_number: 1
                    }
                }
            });

            if (data.success === false) {
                throw new Error("Failed to list tools");
            } else {
                const tools = data.map((record) => ({
                    // cell_identifier: "", // TODO: FIND IDENTIFIER
                    tool_id: record.id,
                    tool_name: record.name,
                    package_name: record.package_name,
                    arguments_object: record.arguments_object,
                    tool_description: record.description,
                    custom_tool: record.custom_tool
                }));

                dispatch(setTools(tools));
            }
        } catch (e) {
            console.error(e);
            // Show error toast
            hotToast.error(
                "We are unable to load the tools at the moment. Please try again later."
            );
        }
    }

    async function loadCollaborationAppDetailsApi(): Promise<CollaborationAppGroup> {
        try {
            const { data } = await httpClient.post(
                `/api/v1/collaboration-app/get-collaboration-app-details`,
                {
                    // auth: {
                    //     access_token: token,
                    // },
                    data: {
                        application_code: applicationCode,
                        hide_sensitive_info: true
                    }
                }
            );

            if (data.success === false) {
                throw new Error("Failed to get collaboration app data");
            } else {
                // set collab app in state
                dispatch(
                    setCollaborationApp({
                        id: data.id,
                        title: data.title,
                        description: data.description,
                        status: data.status,
                        // persona: data,
                        createdAt: data.createdAt,
                        updatedAt: data.updatedAt,
                        json_structure: {
                            watermark: data.json_structure.watermark,
                            app: data.json_structure.app,
                            file: data.json_structure.file,
                            display: data.json_structure.display,
                            activators_required: data.json_structure.activators_required
                        },
                        type: data.type
                    })
                );
                return data;
            }
        } catch (error) {
            throw new Error("Failed to get collaboration app data");
        }
    }

    async function loadUserToolConfigurationApi() {
        try {
            const { data } = await httpClient.post(
                `/api/v1/user-tool-configuration/list-user-tool-configuration`,
                {}
            );

            if (data.success === false) {
                throw new Error("Failed to list tool configurations");
            } else {
                return data.data;

                // if (data.length > 0) {
                // return data.data;
                // let fetched_tool_argument_values_map = new Map();
                // fetched_tool_argument_values_map = new Map(data.map(item => [item.tool_id, item]));
                // }

                // Load collaboration app details
                // loadCollaborationAppDetails();
            }
        } catch (error) {
            console.error(error);
            // show error toast
            hotToast.error(
                "We are unable to load the user tool configuration at the moment. Please try again later."
            );
        }
    }

    async function listPrivateStructuredDbsApi() {
        try {
            dispatch(setTemplateLoading(true));
            const response = await httpClient.post(
                `/api/v1/template-metadata/list-template-metadata`,
                {
                    data: {
                        search_criteria: {}
                    }
                }
            );

            if (response.data.success === false) {
                throw new Error("Failed to get template data");
            }

            dispatch(setTemplateLoadingError(""));
            if (response && response.data) {
                const templateData: TemplateMetadataModel[] = response.data.data;

                dispatch(populateTemplates(templateData));

                return templateData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setTemplateLoadingError("Unable to fetch template data"));
            throw new Error("Failed to get template data");
        } finally {
            dispatch(setTemplateLoading(false));
        }
    }

    async function listTransactionCoordinatorCataloguesApi() {
        try {
            dispatch(setCatalogueLoading(true));
            const response = await httpClient.post(
                `/api/v1/transaction-coordinator-catalogue/list-transaction-coordinator-catalogue`,
                {
                    data: {
                        search_criteria: {},
                        populate: {
                            transaction_coordinator_event: true
                        }
                    }
                }
            );

            if (response.data.success === false) {
                throw new Error("Failed to get catalogue data");
            }

            dispatch(setCatalogueLoadingError(""));
            if (response && response.data) {
                const catalogueData: ITransactionCoordinatorCatalogue[] = response.data.data;

                dispatch(populateCatalogues(catalogueData));

                return catalogueData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setCatalogueLoadingError("Unable to fetch catalogue data"));
            throw new Error("Failed to get template data");
        } finally {
            dispatch(setCatalogueLoading(false));
        }
    }

    async function listTransactionCoordinatorEventsApi() {
        try {
            dispatch(setEventLoading(true));
            const response = await httpClient.post(
                `/api/v1/transaction-coordinator-event/list-transaction-coordinator-event`,
                {
                    data: {
                        search_criteria: {}
                    }
                }
            );

            if (response.data.success === false) {
                throw new Error("Failed to get event data");
            }

            dispatch(setEventLoadingError(""));
            if (response && response.data) {
                const eventData: ITransactionCoordinatorEvent[] = response.data.data;

                dispatch(populateEvents(eventData));

                return eventData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setEventLoadingError("Unable to fetch event data"));
            throw new Error("Failed to get event data");
        } finally {
            dispatch(setEventLoading(false));
        }
    }

    async function listPrivateCampaignTemplatesApi() {
        try {
            dispatch(setCampaignLoading(true));
            const response = await httpClient.post(`/api/v1/campaign/list-campaign-templates`, {
                data: {
                    search_criteria: {},
                    sort: { createdAt: -1 }
                }
            });

            if (response.data.success === false) {
                throw new Error("Failed to get campaign template data");
            }

            dispatch(setCampaignLoadingError(""));
            if (response && response.data) {
                const campaignTemplateData: ICampaign[] = response.data.data;

                dispatch(populateCampaignTemplates(campaignTemplateData));

                return campaignTemplateData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setCampaignLoadingError("Unable to fetch campaign template data"));
            throw new Error("Failed to get campaign template data");
        } finally {
            dispatch(setCampaignLoading(false));
        }
    }

    async function listCampaignsApi(searchCriteria: object) {
        try {
            dispatch(setCampaignsLoading(true));
            const response = await httpClient.post(`/api/v1/campaign/list-campaign`, {
                data: {
                    search_criteria: searchCriteria
                }
            });

            if (response.data.success === false) {
                throw new Error("Failed to get campaign data");
            }

            dispatch(setCampaignsLoadingError(""));
            if (response && response.data) {
                const campaignData: ICampaign[] = response.data.data;
                dispatch(populateCampaigns(campaignData));
                return campaignData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setCampaignsLoadingError("Unable to fetch campaign data"));
            throw new Error("Failed to get campaign data");
        } finally {
            dispatch(setCampaignsLoading(false));
        }
    }

    async function updateCellJsonStructureApi() {
        try {
            if (!nodesDataState.collabApp?.id) {
                return;
            }

            const { data } = await httpClient.post(
                `/api/v1/collaboration-app-group/update-cell-json-structure`,
                {
                    data: {
                        // auth: {
                        //     access_token: token,
                        // },
                        data: {
                            id: nodesDataState.collabApp?.id,
                            json_structure: {
                                ...nodesDataState.collabApp.json_structure,
                                cells: nodesDataState.cells,
                                flows: nodesDataState.flows
                            }
                        }
                    }
                }
            );

            if (data.success === false) {
                throw new Error("Failed to update json cell structure");
            } else {
                return data;
            }
        } catch (error) {
            console.error(error);
            // show error toast
            hotToast.error(
                "We are unable to update the cell json structure at the moment. Please try again later."
            );
        }
    }

    async function updateCollaborationAppApi() {
        try {
            if (!nodesDataState.collabApp?.id) {
                throw new Error("No collaboration app group to update.");
            }
            const promiseActionList: any[] = [];

            const cells = cloneDeep(nodesDataState.cells);

            const payload: any = {
                id: nodesDataState.collabApp?.id,
                json_structure: {
                    watermark: nodesDataState.collabApp.json_structure.watermark,
                    app: nodesDataState.collabApp.json_structure.app,
                    file: nodesDataState.collabApp.json_structure.file,
                    cells: cells.map((cell) => {
                        cell.tool_wishlist = cell.tool_wishlist.map((wishedTool) => {
                            for (const key of Object.keys(
                                wishedTool.selected_tool.arguments_object
                            )) {
                                const argObj = wishedTool.selected_tool.arguments_object[key];
                                if (argObj && argObj.action) {
                                    promiseActionList.push(
                                        httpClient.post(
                                            `/api/v1/collaboration-app-group/set-action-attribute`,
                                            {
                                                collaboration_app_group_id:
                                                    nodesDataState.collabApp?.id,
                                                action: argObj.action,
                                                action_arguments: argObj.default_value
                                            }
                                        )
                                    );
                                }
                                wishedTool.selected_tool.arguments_object[key] = omit(
                                    wishedTool.selected_tool.arguments_object[key],
                                    ["prev_parameter_type"]
                                );
                            }
                            if (Array.isArray(wishedTool.tool_replacements)) {
                                wishedTool.tool_replacements = wishedTool.tool_replacements.map(
                                    (toolR) => {
                                        // Check if toolR and toolR.arguments_object are defined
                                        if (toolR && toolR.arguments_object) {
                                            for (const key of Object.keys(toolR.arguments_object)) {
                                                toolR.arguments_object[key] = omit(
                                                    toolR.arguments_object[key],
                                                    ["prev_parameter_type"]
                                                );
                                            }
                                        }
                                        return toolR; // Return the toolR object, whether modified or not
                                    }
                                );
                            } else {
                                // If tool_replacements is not present, initialize it to an empty array
                                wishedTool.tool_replacements = [];
                            }
                            // wishedTool.tool_replacements =
                            //     wishedTool?.tool_replacements.map(
                            //         (toolR) => {
                            //             for (const key of Object.keys(
                            //                 toolR?.arguments_object
                            //             )) {
                            //                 toolR.arguments_object[key] =
                            //                     omit(
                            //                         toolR.arguments_object[
                            //                             key
                            //                         ],
                            //                         ["prev_parameter_type"]
                            //                     );
                            //             }
                            //             return toolR;
                            //         }
                            //     );
                            return wishedTool;
                        });
                        return cell;
                    }),
                    activators_required: nodesDataState.collabApp.json_structure.activators_required
                }
            };

            if (nodesDataState.collabApp.json_structure.display) {
                payload.json_structure.display = nodesDataState.collabApp.json_structure.display;
            }
            // console.log(cells);
            const { data } = await httpClient.post(
                `/api/v1/collaboration-app-group/update-collaboration-app-group`,
                payload
            );

            if (data.success === false) {
                throw new Error("Failed to update json cell structure");
            } else {
                await Promise.all(promiseActionList);
                return data;
            }
        } catch (error) {
            console.error(error);
            // show error toast
            throw new Error("Failed to update json cell structure");
        }
    }

    async function executeToolApi(payload: {
        toolId: string;
        arguments: any;
        personaId: string;
        testMode: boolean;
        executionToolId?: string;
    }) {
        try {
            const { data } = await httpClient.post(`/api/v1/tool/execute-tool`, {
                tool_id: payload.toolId,
                tool_arguments: payload.arguments,
                persona_id: payload.personaId,
                collaboration_app_group_id: nodesDataState.collabApp?.id,
                test_mode: payload.testMode,
                execution_tool_id: payload.executionToolId
            });

            if (data.success === false) {
                throw new Error("Failed to execute tool");
            } else {
                return data;
            }
        } catch (error) {
            console.error(error);
            // show error toast
            throw new Error(
                "We are unable to execute the tool at the moment. Please try again later."
            );
        }
    }

    async function executeToolWishlistApi(payload: {
        cellId: number;
        toolId: number;
        personaId: string;
    }) {
        try {
            if (!nodesDataState.collabApp?.id) {
                throw new Error("No collaboration app group to update.");
            }
            // First save collaboration app
            await updateCollaborationAppApi();
            /**
             * Call endpoint to execute tool-wishlist
             */
            const { data } = await httpClient.post(
                `/api/v1/collaboration-app-group/test-cell-tool`,
                {
                    data: {
                        collaboration_app_group_id: nodesDataState.collabApp?.id,
                        cell_identifier: payload.cellId,
                        tool_identifier: payload.toolId,
                        persona_id: payload.personaId
                    }
                }
            );

            if (data.success === false) {
                throw new Error("Failed to execute tool wishlist");
            } else {
                return data;
            }
        } catch (error) {
            console.error(error);
            // show error toast
            throw new Error(
                "We are unable to execute the tool wishlist at the moment. Please try again later."
            );
        }
    }

    async function listPersonaApi(search?: any, params?: any) {
        try {
            dispatch(setPersonaLoading(true));
            const personaResponse = await httpClient.post(`/api/v1/persona/list-persona`, {
                data: {
                    search_criteria: search || {},
                    pagination: {
                        rows_per_page: params?.limit || 50,
                        page_number: params?.page || 1
                    },
                    sort: params?.sort || { createdAt: 1 }
                }
            });

            if (personaResponse.data.success === false) {
                throw new Error("Cannot load personas");
            }

            dispatch(setPersonaLoadingError(""));
            if (personaResponse && personaResponse.data) {
                // Add the data to state
                const personaData: PersonaModel[] = personaResponse.data;

                // generalEntityData = personaResponse.data.filter(persona => persona.is_general_entity === "yes");
                // personaData = personaResponse.data.filter(persona => persona.is_general_entity !== "yes");

                // set personas
                dispatch(populatePersonas(personaData));

                // general entities
                // dispatch(setGeneralEntities(generalEntityData));
                return personaData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setPersonaLoadingError("Unable to fetch personas"));
            hotToast.error("Cannot fetch your personas at this time, Please try again later");
        } finally {
            dispatch(setPersonaLoading(false));
        }
    }

    async function getClientApi(payload?: any): Promise<IClient[]> {
        try {
            dispatch(setClientLoading(true));
            const { data } = await httpClient.post("/api/v1/client/list-client", {
                data: {
                    search_criteria: payload?.searchCriteria || {},
                    sort: payload?.sort || { createdAt: -1 }
                }
            });

            if (data.success === false) {
                throw new Error("Failed to get client data");
            }

            dispatch(setClientLoadingError(""));
            if (data && data.data) {
                const clientData: IClient[] = data.data;
                dispatch(populateClients(clientData));
                return clientData;
            } else {
                return [];
            }
        } catch (e) {
            console.error(e);
            dispatch(setClientLoadingError("Unable to fetch client data"));
            throw new Error("Failed to get client data");
        } finally {
            dispatch(setClientLoading(false));
        }
    }

    return {
        httpClient,
        listMemoryApi,
        listToolApi,
        loadCollaborationAppDetailsApi,
        loadUserToolConfigurationApi,
        updateCellJsonStructureApi,
        updateCollaborationAppApi,
        executeToolApi,
        executeToolWishlist: executeToolWishlistApi,
        listPersonaApi,
        listPrivateStructuredDbsApi,
        listPrivateCampaignTemplatesApi,
        listCampaignsApi,
        listTransactionCoordinatorCataloguesApi,
        listTransactionCoordinatorEventsApi,
        getClientApi
    };
}
